class MainCarousel extends HTMLElement {
	static get observedAttributes() {
		return [];
	}

	connectedCallback() {
		const options = {
			root: this,
			rootMargin: "0px",
			threshold: 0.75,
		};
		this.carouselobserver = new IntersectionObserver((entries) => {
			// biome-ignore lint/complexity/noForEach: <explanation>
			entries.forEach(({ isIntersecting, target }) => {
				if (isIntersecting) {
					const id = target.getAttribute("data-id");

					this.dispatchEvent(
						new CustomEvent("slidechange", {
							detail: { id },
							bubbles: true, // Ensure the event bubbles up
							composed: true, // Allow the event to cross the shadow DOM boundary
						})
					);
				}
			});
		}, options);
		this.querySelectorAll("& > picture, & > img").forEach((img, i) => {
			img.setAttribute("data-id", i);
			this.carouselobserver.observe(img);
		});
		// biome-ignore lint/complexity/noForEach: <explanation>
		this.querySelectorAll("picture.combined").forEach((stackedImage) => {
			const images = stackedImage.querySelectorAll("img");
			let imagesLoaded = 0;

			// Set picture's opacity to 0 initially
			stackedImage.style.opacity = 0;

			// Check if all images have been loaded
			// biome-ignore lint/complexity/noForEach: <explanation>
			images.forEach((img) => {
				if (img.complete) {
					imagesLoaded++;
					// If image is already loaded (cached), increase count immediately
				} else {
					img.addEventListener("load", () => {
						imagesLoaded++;
						// Once an image is loaded, increase count
						if (imagesLoaded === images.length) {
							stackedImage.style.opacity = 1;
							// Set opacity to 1 when all images are loaded
						}
					});
				}
			});

			// Edge case for cached images: If all are already loaded, make sure the opacity is set
			if (imagesLoaded === images.length) {
				stackedImage.style.opacity = 1;
			}
		});
	}

	gotTo(id) {
		const target = this.querySelector(`[data-id="${id}"]`);
		this.scrollTo({
			left: target.offsetLeft - (this.clientWidth - target.clientWidth) / 2,
			behavior: "smooth",
		});
	}
}

class CarouselThumbnails extends HTMLElement {
	static get observedAttributes() {
		return ["data-current-id"];
	}

	constructor() {
		super();

		this.active = null;
		this.prevScrollLeft = this.scrollLeft;
	}

	connectedCallback() {
		// biome-ignore lint/complexity/noForEach: <explanation>
		this.querySelectorAll("picture.combined").forEach((stackedImage) => {
			console.log(stackedImage);
			const images = stackedImage.querySelectorAll("img");
			let imagesLoaded = 0;

			// Set picture's opacity to 0 initially
			stackedImage.style.opacity = 0;

			// Check if all images have been loaded
			// biome-ignore lint/complexity/noForEach: <explanation>
			images.forEach((img) => {
				if (img.complete) {
					imagesLoaded++;
					// If image is already loaded (cached), increase count immediately
				} else {
					img.addEventListener("load", () => {
						imagesLoaded++;
						// Once an image is loaded, increase count
						if (imagesLoaded === images.length) {
							stackedImage.style.opacity = 1;
							// Set opacity to 1 when all images are loaded
						}
					});
				}
			});

			// Edge case for cached images: If all are already loaded, make sure the opacity is set
			if (imagesLoaded === images.length) {
				stackedImage.style.opacity = 1;
			}
		});

		const options = {
			root: this,
			rootMargin: "0px",
			threshold: 0.5,
		};
		this.observer = new IntersectionObserver((entries) => {
			// biome-ignore lint/complexity/noForEach: <explanation>
			entries.forEach((entry) => {
				const index = Number.parseInt(
					entry.target.getAttribute("data-index") ?? 1
				);
				if (index === 1) {
					this.updateMask("left", !entry.isIntersecting);
				} else if (index === this.childElementCount) {
					this.updateMask("right", !entry.isIntersecting);
				}
			});
		}, options);
		this.querySelectorAll("button").forEach((button, i) => {
			button.setAttribute("data-id", i);
			button.addEventListener("click", () =>
				this.dispatchEvent(
					new CustomEvent("thumbnailclick", {
						detail: { id: i },
						bubbles: true,
						composed: true,
					})
				)
			);
			this.observer.observe(button);
		});
		this.addEventListener("scroll", this.handleScroll);
		// Listen for slidechange events
		this.closest("image-carousel").addEventListener(
			"slidechange",
			({ detail: { id } }) => {
				this.centerThumbnail(id);
				this.setAttribute("data-current-id", id);
			}
		);
	}
	handleScroll({ target: { scrollLeft, scrollWidth, clientWidth, style } }) {
		const scrolldist = Math.max(
			scrollLeft - (scrollLeft < 0 ? 0 : scrollWidth - clientWidth) / 10,
			-1.75
		);
		if (scrollLeft >= 0 || scrollLeft <= scrollWidth - clientWidth) return;
		style.setProperty(
			`--margin-${scrollLeft < 0 ? "left" : "right"}`,
			String(scrolldist)
		);
	}
	updateMask(dir, show) {
		function transition(startValue, endValue, duration, callback) {
			const startTime = performance.now();

			function animate(time) {
				const elapsed = time - startTime;
				const progress = Math.min(elapsed / duration, 1);

				callback(startValue + (endValue - startValue) * progress);

				progress < 1 && requestAnimationFrame(animate);
			}

			requestAnimationFrame(animate);
		}

		transition(+show, +!show, 200, (v) =>
			this.style.setProperty(`--mask-opacity-${dir}`, v)
		);
	}
	centerThumbnail(id) {
		const target = this.querySelector(`button[data-id="${id}"]`);
		if (!target) return;
		const left = this.scrollLeft > this.prevScrollLeft ? 0 : 3.5 * 16;
		this.scrollTo({
			left: target.offsetLeft - (this.clientWidth - left) / 2,
			behavior: "smooth",
		});
		this.prevScrollLeft = this.scrollLeft;
	}
	attributeChangedCallback(name, old, value) {
		if (old === value) return;
		if (name === "data-current-id") {
			// biome-ignore lint/complexity/noForEach: <explanation>
			this.querySelectorAll("button").forEach((button) =>
				button.toggleAttribute(
					"active",
					button.getAttribute("data-id") === value
				)
			);
		}
	}
}

class ImageCarousel extends HTMLElement {
	connectedCallback() {
		this.addEventListener("thumbnailclick", ({ detail: { id } }) =>
			this.querySelector("main-carousel").gotTo(id)
		);
	}
}
customElements.define("carousel-thumbnails", CarouselThumbnails);
customElements.define("image-carousel", ImageCarousel);
customElements.define("main-carousel", MainCarousel);
